import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { API_ENDPOINTS } from '../config/api';
import ProgramPGYUtils from '../utils/programPGYUtils';
import ExpandableUserSelector from './ExpandableUserSelector';

const OnboardingAssignModal = ({ show, onHide, target, onAssigned }) => {
  const token = localStorage.getItem('token');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [instructions, setInstructions] = useState('');
  const [forceRequirement, setForceRequirement] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.USERS}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setAvailableUsers(data);
      } catch (e) {
        setError(e.message);
      }
    };
    const fetchProgramPGY = async () => {
      try {
        const tok = token;
        if (!tok) return;
        const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!res.ok) {
          const fallback = Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }));
          setPgyLevelOptions(fallback);
          setMaxPGYLevel(5);
          return;
        }
        const data = await res.json();
        const programId = data?.id;
        if (!programId) {
          const fallback = Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }));
          setPgyLevelOptions(fallback);
          setMaxPGYLevel(5);
          return;
        }
        const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
        setPgyLevelOptions(options);
        setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
      } catch (_e) {
        const fallback = Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }));
        setPgyLevelOptions(fallback);
        setMaxPGYLevel(5);
      }
    };
    if (show) {
      setError('');
      setSelectedUsers([]);
      setDueDate('');
      setInstructions('');
      setForceRequirement(false);
      fetchUsers();
      fetchProgramPGY();
    }
  }, [show, token]);

  const handleAssign = async () => {
    try {
      if (selectedUsers.length === 0) {
        setError('Select at least one user');
        return;
      }
      setLoading(true);
      const body = { userIds: selectedUsers };
      if (dueDate) body.dueDate = dueDate;
      if (instructions) body.instructions = instructions;
      if (forceRequirement) body.forceRequirement = true;
      if (target?.type === 'package') body.packageId = target.id;
      if (target?.type === 'document') body.documentId = target.id;
      if (target?.type === 'documentRequest') body.documentRequestId = target.id;
      if (target?.type === 'form') body.formId = target.id;
      
      console.log('Assignment request body:', body);
      console.log('Force requirement enabled:', forceRequirement);
      
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      
      console.log('Assignment response status:', res.status);
      
      if (!res.ok) {
        const errorData = await res.text();
        console.error('Assignment error response:', errorData);
        throw new Error(`Failed to assign: ${res.status} ${res.statusText}`);
      }
      
             const result = await res.json();
       console.log('Assignment successful:', result);
       
       // If force requirement was enabled, show a message about logout requirement
       if (forceRequirement) {
         console.log('Force requirement was enabled, clearing localStorage for assigned users');
         alert('Assignment created successfully! Users will be redirected to the document completion page on their next login. They may need to log out and log back in for the changes to take effect immediately.');
       }
       
       onAssigned && onAssigned();
       onHide();
    } catch (e) {
      console.error('Assignment error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className='border-0 px-md-4'>
        <div className="d-flex flex-column justify-content-start w-50 gap-2">
        <Modal.Title>Assign {target?.type === 'package' ? 'Package' : target?.type === 'documentRequest' ? 'Document Request' : target?.type === 'form' ? 'Form' : 'Document'}</Modal.Title>
        <div className="text-hl"></div> </div>
      </Modal.Header>
      <Modal.Body className="px-md-4">
        {error && (<div className="alert alert-danger py-2">{error}</div>)}
        
        <Row>          
          <Col md={6}>
              
             <FloatingLabel controlId="floatingDate" label="Due date (optional)" className="mb-3">
              <Form.Control type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </FloatingLabel>
           
          </Col>
          <Col md={6}>
            <ExpandableUserSelector
              availableUsers={availableUsers}
              selectedUsers={selectedUsers}
              onSelectionChange={setSelectedUsers}
              pgyLevelOptions={pgyLevelOptions}
              maxPGYLevel={maxPGYLevel}
              helperText="Select users to assign this item."
            />
          </Col>

          <Col md={12}>
          <div className="d-flex flex-column">
          <FloatingLabel controlId="floatingTextarea" label="Instructions (optional)" className="mb-3">
              <Form.Control 
                as="textarea" 
                rows={4}
                placeholder="Add specific instructions for completing this assignment..."
                value={instructions} 
                onChange={(e) => setInstructions(e.target.value)} 
                style={{ height: '100px' }}
              />
              </FloatingLabel>
          
            <Form.Check
              type="checkbox"
              id="force-requirement"
              checked={forceRequirement}
              onChange={(e) => setForceRequirement(e.target.checked)}
              label={
                <div>
                  <strong>Force Document Requirement</strong>
                  <div className="text-muted small">
                    When enabled, users will be redirected to the document completion page on their next login until this assignment is completed.
                  </div>
                </div>
              }
            />
            </div>
          </Col>

        </Row>

      </Modal.Body>
      <Modal.Footer className="px-md-4 border-0">
        <Button variant="secondary" size="sm" onClick={onHide} disabled={loading}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleAssign} disabled={loading || selectedUsers.length === 0}>
          {loading ? 'Assigning...' : 'Assign'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OnboardingAssignModal;


